import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../ui/alert-dialog';
import { Loader2, Mic, Square, Shield, ShieldCheck, Fingerprint, Trash2, Sparkles } from 'lucide-react';
import { useWorkspace } from '@/contexts/context';
import { supabase } from '@saas-boilerplate/shared';
import { invokeEdgeFn } from '@/lib/edge-functions';
import { useMediaRecording } from '@/lib/voice/use-media-recording';
import { encodeWav16kMono, wavSeconds } from '@/lib/voice/encode-wav';

const BUCKET = 'voice-samples';
const SAMPLE_MAX_SECONDS = 30;
const SAMPLE_MIN_SECONDS = 8;

// A varied-phonetics passage so a ~20s read covers a broad range of sounds.
const ENROLMENT_PASSAGE =
  'The early morning fog rolled quietly over the harbour while six brave fishermen hauled their heavy nets ashore. ' +
  'Jasmine bought a dozen fresh oranges, two jugs of milk, and a warm loaf of crusty bread from the village market. ' +
  'Whenever the autumn wind shifts, the old oak trees creak and whisper secrets that only patient listeners ever hear.';

type Voiceprint = {
  id: string;
  enrolled_via: string;
  sample_count: number;
  consent_recorded_at: string;
  updated_at: string;
};
type Job = { id: string; status: string; failure_reason: string | null; finished_at: string | null };
type BuildRequest = {
  id: string;
  label: string;
  meetingTitle: string | null;
  meetingDate: string | null;
  requesterName: string;
};

export function VoiceSettings() {
  const { workspace, userProfile } = useWorkspace();
  const workspaceId = workspace?.id || '';
  const userId = userProfile?.id || '';

  const [loading, setLoading] = useState(true);
  const [voiceprint, setVoiceprint] = useState<Voiceprint | null>(null);
  const [job, setJob] = useState<Job | null>(null);
  const [buildRequests, setBuildRequests] = useState<BuildRequest[]>([]);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);

  const [recordOpen, setRecordOpen] = useState(false);
  const [recordMode, setRecordMode] = useState<'create' | 'improve'>('create');
  const [processing, setProcessing] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async () => {
    if (!workspaceId || !userId) return;
    const [{ data: vp }, { data: jobs }, { data: reqs }] = await Promise.all([
      supabase
        .from('voiceprints')
        .select('id, enrolled_via, sample_count, consent_recorded_at, updated_at')
        .eq('workspace_id', workspaceId)
        .eq('user_id', userId)
        .is('deleted_at', null)
        .maybeSingle(),
      supabase
        .from('voiceprint_extraction_jobs')
        .select('id, status, failure_reason, finished_at')
        .eq('workspace_id', workspaceId)
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1),
      supabase
        .from('voiceprint_build_requests')
        .select('id, source_diarisation_label, requested_by_user_id, created_at, source_meeting:source_meeting_id(starts_at, series:series_id(title))')
        .eq('target_user_id', userId)
        .eq('status', 'pending')
        .order('created_at', { ascending: false }),
    ]);
    setVoiceprint((vp as Voiceprint | null) ?? null);
    setJob(((jobs as Job[] | null) ?? [])[0] ?? null);

    // Resolve requester names separately - the FK is to auth.users, not profiles, so it can't be embedded.
    const rows = ((reqs as Array<Record<string, any>> | null) ?? []);
    const requesterIds = [...new Set(rows.map((r) => r.requested_by_user_id).filter(Boolean))];
    let names: Record<string, string> = {};
    if (requesterIds.length) {
      const { data: profs } = await supabase.from('profiles').select('id, full_name').in('id', requesterIds);
      names = Object.fromEntries(((profs as Array<{ id: string; full_name: string }> | null) ?? []).map((p) => [p.id, p.full_name]));
    }
    setBuildRequests(
      rows.map((r) => ({
        id: r.id as string,
        label: (r.source_diarisation_label as string) || '',
        meetingTitle: (r.source_meeting?.series?.title as string) ?? null,
        meetingDate: (r.source_meeting?.starts_at as string) ?? null,
        requesterName: names[r.requested_by_user_id as string] || 'A meeting owner',
      })),
    );
    setLoading(false);
  }, [workspaceId, userId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Poll while an extraction job is in flight.
  const jobActive = job?.status === 'queued' || job?.status === 'running';
  useEffect(() => {
    if (jobActive && !pollRef.current) {
      pollRef.current = setInterval(() => void load(), 3000);
    }
    if (!jobActive && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [jobActive, load]);

  const startAutoEnrol = useCallback(async () => {
    if (!workspaceId) return;
    if (!voiceprint && !consent) {
      toast.error('Please tick the consent box first.');
      return;
    }
    setBusy(true);
    try {
      await invokeEdgeFn('enrol-voiceprint-from-past', { workspace_id: workspaceId, consent: true });
      toast.success('Enrolment started - this can take a minute or two.');
      await load();
    } catch (e: any) {
      toast.error(e?.message || 'Could not start enrolment');
    } finally {
      setBusy(false);
    }
  }, [workspaceId, voiceprint, consent, load]);

  const handleRecorded = useCallback(
    async (blob: Blob) => {
      if (!workspaceId || !userId) return;
      setProcessing(true);
      try {
        const wav = await encodeWav16kMono(blob);
        if (wavSeconds(wav) < SAMPLE_MIN_SECONDS) {
          toast.error(`That clip was too short - please record at least ${SAMPLE_MIN_SECONDS} seconds.`);
          return;
        }
        const path = `${workspaceId}/${userId}/${crypto.randomUUID()}.wav`;
        const { error: upErr } = await supabase.storage
          .from(BUCKET)
          .upload(path, wav, { contentType: 'audio/wav', upsert: false });
        if (upErr) throw new Error(upErr.message);
        await invokeEdgeFn('enrol-voiceprint-explicit', {
          workspace_id: workspaceId,
          sample_path: path,
          consent: true,
          mode: recordMode,
        });
        toast.success(recordMode === 'improve' ? 'Sample added to your voiceprint.' : 'Voiceprint enrolled.');
        setRecordOpen(false);
        await load();
      } catch (e: any) {
        toast.error(e?.message || 'Enrolment failed');
      } finally {
        setProcessing(false);
      }
    },
    [workspaceId, userId, recordMode, load],
  );

  const deleteVoiceprint = useCallback(async () => {
    if (!workspaceId) return;
    setBusy(true);
    try {
      await invokeEdgeFn('delete-voiceprint', { workspace_id: workspaceId });
      toast.success('Voiceprint deleted.');
      setConsent(false);
      await load();
    } catch (e: any) {
      toast.error(e?.message || 'Could not delete voiceprint');
    } finally {
      setBusy(false);
    }
  }, [workspaceId, load]);

  const respondToRequest = useCallback(
    async (requestId: string, decision: 'accept' | 'decline') => {
      setBusy(true);
      try {
        const res = await invokeEdgeFn<{ built?: boolean }>('respond-voiceprint-build-request', {
          request_id: requestId,
          decision,
        });
        if (decision === 'decline') {
          toast.success('Dismissed.');
        } else if (res?.built) {
          toast.success("Voiceprint built - you'll be recognised automatically next time.");
        } else {
          toast.success("Thanks - we couldn't get clean audio from that meeting. Record a short sample to finish.");
        }
        await load();
      } catch (e: any) {
        toast.error(e?.message || 'Could not respond to the request');
      } finally {
        setBusy(false);
      }
    },
    [load],
  );

  const openRecorder = (mode: 'create' | 'improve') => {
    setRecordMode(mode);
    setRecordOpen(true);
  };

  if (loading) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const enrolledViaLabel =
    voiceprint?.enrolled_via === 'recorded'
      ? 'recorded sample'
      : voiceprint?.enrolled_via === 'extracted_from_meeting'
        ? 'past meetings'
        : 'upload';

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h2 className="text-xl font-semibold">Voice</h2>
        <p className="text-sm text-muted-foreground">
          Your voiceprint lets upstand.ai attribute what you say in shared-room meetings, where one
          device captures several voices at once.
        </p>
      </div>

      {/* Owner-initiated consent request(s): build a voiceprint from a meeting where an owner identified you. */}
      {buildRequests.length > 0 && (
        <Card className="border-primary/40 bg-primary/5">
          <CardHeader>
            <CardTitle className="text-base">Confirm your voice</CardTitle>
            <CardDescription>
              A meeting owner identified you on a shared mic. Build a voiceprint so upstand.ai recognises you
              automatically next time. It&apos;s stored as a numeric signature (not a recording), recognised as
              biometric data under GDPR/BIPA, used only in this workspace, and deletable anytime.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {buildRequests.map((r) => (
              <div key={r.id} className="rounded-md border border-border bg-background px-3 py-3 text-sm">
                <p>
                  <span className="font-medium">{r.requesterName}</span> identified your voice
                  {r.meetingTitle ? <> in “{r.meetingTitle}”</> : <> in a recent meeting</>}
                  {r.meetingDate ? <> on {new Date(r.meetingDate).toLocaleDateString()}</> : null}.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => void respondToRequest(r.id, 'accept')} disabled={busy}>
                    <ShieldCheck className="mr-2 h-4 w-4" /> Consent &amp; build
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => void respondToRequest(r.id, 'decline')} disabled={busy}>
                    No thanks
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Status */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="text-base">Your voiceprint</CardTitle>
            {voiceprint ? (
              <Badge variant="secondary" className="gap-1">
                <ShieldCheck className="h-3.5 w-3.5" /> Enrolled
              </Badge>
            ) : (
              <Badge variant="outline">Not enrolled</Badge>
            )}
          </div>
          <CardDescription>
            {voiceprint
              ? `Enrolled from ${enrolledViaLabel} · ${voiceprint.sample_count} sample${voiceprint.sample_count === 1 ? '' : 's'} · consent recorded ${new Date(voiceprint.consent_recorded_at).toLocaleDateString()}`
              : jobActive
                ? 'Building your voiceprint from your past meetings…'
                : 'Build your voiceprint to be auto-identified in future room-mic meetings.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {voiceprint && (
            <div className="flex flex-col items-center justify-center py-4 text-center">
              <div className="relative inline-flex items-center justify-center" aria-hidden>
                <Shield className="h-48 w-48 text-primary/15" strokeWidth={1} fill="currentColor" />
                <Fingerprint className="absolute h-24 w-24 -translate-y-2 text-primary" strokeWidth={1.25} />
              </div>
              <p className="mt-1 text-sm font-medium text-primary">Voiceprint enrolled</p>
              <p className="text-xs text-muted-foreground">
                upstand.ai can recognise your voice in shared-room meetings.
              </p>
            </div>
          )}
          {jobActive && (
            <div className="flex items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Enrolment in progress - you can leave this page; we&apos;ll email you the result either way.
            </div>
          )}
          {!jobActive && job?.status === 'no_qualifying_audio' && !voiceprint && (
            <div className="rounded-md border border-amber-300/60 bg-amber-50/80 px-3 py-2 text-sm dark:border-amber-700/60 dark:bg-amber-950/30">
              We couldn&apos;t find clean enough audio in your past meetings. Please record a short sample instead.
            </div>
          )}
          {!jobActive && job?.status === 'failed' && !voiceprint && (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              {job.failure_reason || 'Enrolment failed.'} You can try recording a sample instead.
            </div>
          )}

          {voiceprint ? (
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => openRecorder('improve')} disabled={busy}>
                <Sparkles className="mr-2 h-4 w-4" /> Add a sample
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" disabled={busy}>
                    <Trash2 className="mr-2 h-4 w-4" /> Delete voiceprint
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete your voiceprint?</AlertDialogTitle>
                    <AlertDialogDescription>
                      It will be removed from upstand.ai and our provider within 24 hours. Past meetings keep
                      their attribution; future room-mic meetings will fall back to “Guest” for you until you
                      re-enrol.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => void deleteVoiceprint()}>Delete</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          ) : (
            <>
              <Separator />
              <div className="flex items-start gap-2">
                <Checkbox
                  id="voice-consent"
                  checked={consent}
                  onCheckedChange={(c) => setConsent(c === true)}
                  className="mt-0.5"
                  disabled={busy || jobActive}
                />
                <Label htmlFor="voice-consent" className="text-sm font-normal leading-relaxed text-muted-foreground">
                  I consent to upstand.ai building a voiceprint from my voice data for speaker attribution in this
                  workspace. It&apos;s stored as a numeric signature (not a recording), used only here, recognised
                  under GDPR/BIPA as biometric data, and I can delete it at any time.
                </Label>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => void startAutoEnrol()} disabled={busy || jobActive || !consent}>
                  {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Enrol from past meetings
                </Button>
                <Button
                  variant="outline"
                  onClick={() => openRecorder('create')}
                  disabled={busy || jobActive || !consent}
                >
                  <Mic className="mr-2 h-4 w-4" /> Record a sample
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <RecordSampleDialog
        open={recordOpen}
        onOpenChange={(o) => !processing && setRecordOpen(o)}
        processing={processing}
        onRecorded={handleRecorded}
      />
    </div>
  );
}

function RecordSampleDialog({
  open,
  onOpenChange,
  processing,
  onRecorded,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  processing: boolean;
  onRecorded: (blob: Blob) => void | Promise<void>;
}) {
  const { recording, elapsedSeconds, unsupported, errorMessage, start, stop, cancel } = useMediaRecording({
    kind: 'audio',
    maxSeconds: SAMPLE_MAX_SECONDS,
    onComplete: (r) => void onRecorded(r.blob),
  });

  // Discard any in-flight take when the dialog closes.
  useEffect(() => {
    if (!open && recording) cancel();
  }, [open, recording, cancel]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Record a voice sample</DialogTitle>
          <DialogDescription>
            Read the passage below aloud at a natural pace. Aim for about 20 seconds - the more varied speech,
            the better the match.
          </DialogDescription>
        </DialogHeader>

        <p className="rounded-md border border-border bg-muted/40 p-4 text-sm leading-relaxed">{ENROLMENT_PASSAGE}</p>

        {unsupported ? (
          <p className="text-sm text-destructive">
            Recording isn&apos;t supported in this browser. Try Chrome, Edge, or Firefox.
          </p>
        ) : (
          <div className="flex items-center justify-between">
            <span className="font-mono text-sm tabular-nums text-muted-foreground">
              {recording ? `Recording… ${elapsedSeconds}s` : processing ? 'Processing…' : 'Ready'}
            </span>
            {processing ? (
              <Button disabled>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Enrolling
              </Button>
            ) : recording ? (
              <Button variant="destructive" onClick={stop}>
                <Square className="mr-2 h-4 w-4" /> Stop &amp; submit
              </Button>
            ) : (
              <Button onClick={start}>
                <Mic className="mr-2 h-4 w-4" /> Start recording
              </Button>
            )}
          </div>
        )}
        {errorMessage && <p className="text-sm text-destructive">{errorMessage}</p>}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={processing}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
